<script>
/** NOTE */
/** 
 * This component can be used for the following use cases: 
 * 1. Table CRUD, this is the default setting 
 *      Example: Table Jenis Kendaraan (Data Customer)
 * 2. Table Read Only, make sure to set the following props to false
       enableAddButton: false,
        enableDeleteButton: false,
        enableCheckbox: false,
        Example: Table Fuel Raw history (QC Perangkat)
  3.  Table for selection with Checkbox, 
        enableAddButton: false,
        enableDeleteButton: false,
        enableCheckbox: true,
        Example: Table selection in SMS Gateway (QC Perangkat) inside modal
  
 * CUSTOM COLUMN, if you want to customize the column/field by using <template>, make sure to set
  customColumn:true  inside fields.
  Example: icon field in FleetCategory/ Kategori Kendaraan, master SOP
  {
    key: 'name',
    label: 'Nama',
    sortable: true,
    editable: true,
    type: 'text',
    customColumn: true
  },
 * 
*/

import { Helper, debounce } from '@/mixin/helper'
import McButton from '@/components/McButton'

export default {
  components: {
    McButton,
  },
  mixins: [Helper],
  props: {
    fields: { required: true },
    apiClient: { required: true },
    limitOptions: {
      default: () => [10, 25, 50, 100],
    },
    bulkDeleteApi: {
      type: Boolean,
      default: false,
    },
    searchable: { type: Boolean },
    busy: { type: Boolean },
    tableTitle: { type: String },
    withCard: { type: Boolean },
    disabled: { type: Boolean },
    showNoData: { type: Boolean },
    fieldException: { type: Array, required: false },
    trackBy: { default: 'id' },
    idIsString: { default: false },
    enableAddButton: { default: true },
    enableDeleteButton: { default: true },
    isEnableEditButton: { default: true },
    enableCheckbox: { default: false },
    customFilters: { default: () => {} },
    multiPayload: { default: false },
    hidePagination: { default: false },
    addWithModal: { type: Function },
    customSearch: { default: '' },
    sortBy: { default: '' },
    useThousandFormat: { default: true },
  },
  data() {
    return {
      page: 1,
      limit: this.limitOptions[0] ?? 10,
      totalCount: 0,
      isBusy: true,
      search: '',
      sorts: [],
      items: [],
      ids: [],
      rowUpdate: {},
      isEditingRow: null,
      isAdding: false,
      isSubmitting: false,
      selectedIds: {},
      selectedIdsLength: 0,
      selectedAll: false,
      formValues: {},
      vehicleCategories: [],
    }
  },
  watch: {
    customFilters(newVal, oldVal) {
      if (
        Object.values(newVal).every((e) => e === undefined) ||
        Object.values(newVal).every((e) => Object.values(oldVal).includes(e))
      )
        return
      let payload = {}
      Object.entries(newVal).forEach(([key, val]) => {
        payload[`${key}`] = val
      })
      this.getData(payload)
    },
  },
  computed: {
    disabledGlobal() {
      return this.disabled || this.isEditingRow !== null || this.isBusy
    },
    startData() {
      return this.limit * this.page - this.limit + 1
    },
    endData() {
      const _endData = this.limit * this.page
      return this.totalCount < _endData ? this.totalCount : _endData
    },
    // Fields with added defaults
    computedFields() {
      return [
        {
          key: 'checkbox',
          label: '',
        },
        ...this.fields,
        {
          key: 'action',
          label: '',
          thClass: 'mc-action-col-head',
          tdClass: 'mc-action-cell',
        },
      ]
    },
  },
  async mounted() {
    if (this.showNoData) {
      this.isBusy = false
      return
    }

    await this.$nextTick()
    await this.getData({
      limit: this.limit,
      page: this.page,
    })
  },
  methods: {
    transformCellValue(value) {
      if (value === false) return ''
      if (typeof value === 'number') return this.thousandFormat(value)
      return value
    },
    getInputInitialValue({ item, field }) {
      // get value from callback in field setting
      // can be used for formatting select input
      if (field.customValueFn) {
        return field.customValueFn(item) || false
      }

      // get value from item
      if (typeof item[field.key] !== 'undefined') {
        return item[field.key]
      }

      return false
    },
    showSortIcon(data, order) {
      const currentColumnSort = this.sorts.find((sort) => sort.key === data.field.key)
      if (!currentColumnSort) {
        return true
      }

      return currentColumnSort.key === data.field.key && currentColumnSort.order === order
    },
    // TECHDEBT: Only supports resourceful format
    // getData methods can be improved to support user-supplied data
    async getData(opts) {
      if (
        this.customFilters &&
        !Object.keys(this.customFilters).every((e) => Object.keys(opts).includes(e))
      )
        return
      if (!this.apiClient?.get) {
        this.isBusy = false
        return
      }

      this.isBusy = true
      const res = await this.apiClient.get(opts)
      if (!res) return

      let ids
      if (
        Object.keys(res.data).includes('paginated_result') &&
        Array.isArray(res.data.paginated_result)
      ) {
        ids = res.data.paginated_result?.map((e) => e[`${this.trackBy}`])
        res.data.paginated_result?.forEach((e) => {
          if (e.id) return
          e['id'] = e.vehicle_type_id ?? e[`${this.trackBy}`]
        })
      } else if (Array.isArray(res.data)) {
        ids = res.data?.map((e) => e[`${this.trackBy}`])
        res.data?.forEach((e) => {
          if (e.id) return
          e['id'] = e.vehicle_type_id ?? e[`${this.trackBy}`]
        })
      }

      this.items = res.data.paginated_result ?? res.data
      if (this.fields.find((e) => e.isEditable || e.editable)) {
        this.items.forEach((val) => {
          if (val.can_edit === undefined) val['can_edit'] = true
        })
      }

      this.ids = res.data.ids ?? ids
      this.page = res.metadata?.page ?? res.page
      this.limit = res.metadata?.count < this.limit ? this.limit : res.metadata?.count
      this.totalCount = res.metadata?.total_count ?? res.total_count
      const customLabels = this.fields.filter((e) => e.customLabelFn)
      customLabels?.forEach((data) => {
        this.customLabelFn(data, data.key)
      })
      if (res.data.paginated_result?.length === 0 && !this.search) {
        this.$emit('available-change', false)
      }

      this.isBusy = false
    },

    customLabelFn(fields, ...key) {
      this.items?.forEach((data) => {
        data[`${key}`] = fields.customLabelFn(data)
      })
    },

    handleSearchChange: debounce(async function (_search) {
      this.selectedAll = false
      this.search = _search
      if (this.search?.length > 0 && this.search?.length < 3) return
      const opts = {
        limit: this.limit <= 10 ? 10 : this.limit,
        search: _search != '' ? _search : undefined,
        page: 1,
        sorts: this.sorts.map((sort) => `${sort.key} ${sort.order}`),
      }
      await this.getData(opts)
      this.handleSelectAllOnPaginationChange()
      this.$refs['search-input-ref'].$el.focus()
    }, 500),

    handleClearSearch() {
      this.handleSearchChange(undefined)
    },

    async handleLimitChange(_limit) {
      if (_limit < 10) return
      this.limit = _limit < 10 ? 10 : _limit
      let opts = {
        limit: _limit,
        search: this.search != '' ? this.search : undefined,
        page: 1,
        sorts: this.sorts.map((sort) => `${sort.key} ${sort.order}`),
      }
      if (this.customFilters) {
        opts = { ...opts, ...this.customFilters }
      }
      await this.getData(opts)
      this.handleSelectAllOnPaginationChange()
    },

    handleSelectAllOnPaginationChange() {
      const selectedIds = []
      Object.entries(this.selectedIds).forEach(([key, val]) => {
        if (!val && key) return
        selectedIds.push(key)
      })
      this.selectedAll =
        this.ids.every((id) => selectedIds.includes(id.toString())) && selectedIds.length > 0
      this.selectedIdsLength = selectedIds?.length
    },

    async handlePageChange(newPage) {
      let opts = {
        page: newPage,
        search: this.search != '' ? this.search : undefined,
        limit: this.limit,
        sorts: this.sorts.map((sort) => `${sort.key} ${sort.order}`),
      }
      if (this.customFilters) {
        opts = { ...opts, ...this.customFilters }
      }
      await this.getData(opts)
      this.handleSelectAllOnPaginationChange()
    },

    async handleSortChange(data) {
      // If this function is called without data params,
      // remove sorting (revert to default)
      if (!data) {
        this.sorts = []
        return
      }

      // If field not sortable or If currently editing a field, disable sort
      if (!data?.field?.isSortable) return
      if (this.isEditingRow !== null) return

      // Get index of current column sort, if any
      const currentColumnSortIndex = this.sorts.findIndex((sort) => sort.key === data.field.key)
      // Get the sorting data (key, order)
      const currentColumnSort = this.sorts[currentColumnSortIndex]
      const newSorts = [...this.sorts]
      // This switch case is used to toggle between these conditions:
      // default - asc - desc - default (repeat)
      switch (currentColumnSort?.order) {
        case 'asc':
          newSorts[currentColumnSortIndex] = {
            key: currentColumnSort.key,
            order: 'desc',
          }
          break

        case 'desc':
          newSorts.splice(currentColumnSortIndex, 1)
          break

        default:
          newSorts.push({ key: data.field.key, order: 'asc' })
          break
      }

      this.sorts = [...newSorts]
      await this.$nextTick()

      await this.getData({
        limit: this.limit,
        page: 1,
        search: this.search != '' ? this.search : undefined,
        sorts: this.sorts.map((sort) => `${sort.key} ${sort.order}`),
      })
    },

    async handleEditStart(data) {
      if (this.selectedIdsLength) this.handleUnselectAll()
      if (this.isEditingRow != null) return
      // refetch single data from backend for data integrity
      const refetchedData = await this.apiClient.show(data.item.id)
      if (!refetchedData) return

      if (refetchedData.vehicle_type_id) {
        refetchedData['id'] = refetchedData.vehicle_type_id
      }

      if (refetchedData.custom_interval_id) {
        refetchedData['id'] = refetchedData.custom_interval_id
      }

      if (refetchedData.device_status_id) {
        refetchedData['id'] = refetchedData.device_status_id
      }

      if (refetchedData.metabase_dashboard_link_id) {
        refetchedData['id'] = refetchedData.metabase_dashboard_link_id
      }

      if (this.fieldException?.length > 0) {
        this.fields.forEach((item) => {
          if (this.fieldException?.includes(item.key)) {
            item.editable = false
          }
        })
      }

      // update the refetched data (row) in the table
      // map is used since 'this.computedItems[index] = {}' will not work
      this.items = this.items.map((item) => {
        if (item.id == data.item.id) {
          return refetchedData.data ?? refetchedData
        }
        return item
      })

      this.isEditingRow = data.index
      await this.$nextTick()
      this.$emit('changeData')
      this.$refs[`${this.fields[0].key}-ref`]?.$el.querySelector('input').focus()
    },

    handleEditCancel() {
      if (this.isSubmitting) return
      if (this.isAdding) {
        const [, ...newItems] = this.items
        this.items = newItems
        this.isAdding = false
      }
      this.isEditingRow = null
    },

    // TECHDEBT: transformFields will be on formulate input instead of here
    transformFields(formValues, fields) {
      const formValuesKeys = Object.keys(formValues)
      const validKeys = formValuesKeys.filter((key) => formValues[key] !== false)

      return Object.assign(
        {},
        ...validKeys.map((key) => {
          const currentField = fields.find((item) => item.key == key)
          const value = formValues[key]

          if (currentField && currentField._type === 'number') {
            const isValid = Boolean(value) || value === '0' || value === 0
            return { [key]: isValid ? Number(value) : null }
          } else if (currentField && currentField.type === 'text') {
            return { [key]: formValues[key].trim().replace(/ {2}/g, ' ') }
          }

          return { [key]: value }
        })
      )
    },

    async handleSubmit(data) {
      if (this.isSubmitting) {
        this.$toast.warning('Mohon tunggu')
        return
      }

      this.isSubmitting = true
      // Cast fields to correct types
      const transformedData = this.transformFields(data, this.fields)

      let newData = null

      if (this.isAdding) {
        newData = await this.submitStore(transformedData)
      } else {
        newData = await this.submitUpdate(transformedData, this.items[this.isEditingRow])
      }

      // If newData is false, it means there is an error
      if (newData === false) {
        this.isSubmitting = false
        return
      }

      // If newData is null, it means there is no change
      // Replace data on the table
      if (newData) {
        this.items.splice(0, 1, newData)
      }

      this.isEditingRow = null
      this.isAdding = false
      this.isSubmitting = false
    },

    async submitStore(data) {
      // Handle icon data if category reference exists
      if (!data['icon'] && this.$refs['category-ref']) {
        data['icon'] =
          this.$refs['category-ref'].$children[0].$children[0]?.value.icon?.value ||
          this.$refs['category-ref'].$children[0].$children[0]?.value.icon
      }

      if (data['illustration']) {
        data.icon = data?.illustration
      }

      const result = await this.apiClient.store(data)

      // Only handle success case (200)
      if (result?.status === 200 || result?.status === true || result?.status === 201) {
        this.$toast.success('Data berhasil ditambahkan')

        return await this.getData({
          limit: this.limit,
          page: 1,
          search: this.search !== '' ? this.search : undefined,
          sorts: this.sorts.map((sort) => `${sort.key} ${sort.order}`),
        })
      }

      // Return error for any non-200 status
      // Handle 404 status specifically
      if (result?.data?.status === 404 || result?.status === 404) {
        this.$toast.error('Some data not found')
        return false
      }
      this.$toast.error(result?.data?.errors || result?.errors)
      return false
    },

    async submitUpdate(data, oldData) {
      const result = await this.apiClient.update(oldData.id, data)

      // Only handle success case (200)
      if (result?.status === 200 || result?.status === true) {
        this.$toast.success('Data berhasil diubah')

        return await this.getData({
          limit: this.limit,
          page: 1,
          search: this.search !== '' ? this.search : undefined,
          sorts: this.sorts.map((sort) => `${sort.key} ${sort.order}`),
        })
      }

      // Return error for any non-200 status
      if (result?.data?.status === 404 || result?.status === 404) {
        this.$toast.error('Some data not found')
        return false
      }
      this.$toast.error(result?.data?.errors || result?.errors)
      return false
    },

    // eslint-disable-next-line no-unused-vars
    handleFailedValidation(errors) {
      return this.$toast.error('Terdapat kesalahan format pada input anda')
    },

    filterUnchangedData(newValue, oldValue) {
      const keys = new Set([...Object.keys(newValue), ...Object.keys(oldValue)])
      const _newValue = {}
      keys.forEach((key) => {
        if (key == 'id') return
        if (oldValue[key] == newValue[key]) return
        _newValue[key] = newValue[key]
      })

      const entries = Object.entries(_newValue)
      const hasChanged = entries.length > 0 && entries.every((value) => value)
      return [_newValue, hasChanged]
    },

    async handleDelete() {
      const _selected = Object.keys(this.selectedIds).filter((key) => this.selectedIds[key])
      const finalSelections = []

      for (const iterator of _selected) {
        if (this.idIsString) {
          if (!this.multiPayload) finalSelections.push(iterator)
          else finalSelections.push(this.items?.find((e) => iterator === e[this.trackBy]))
        } else {
          const intValue = parseInt(iterator, 10)
          finalSelections.push(intValue)
        }
      }

      let res = null

      if (this.bulkDeleteApi) {
        // Check if deleteMany method exists
        res = await this.apiClient.delete({
          ids: finalSelections,
        })
      } else {
        res = await Promise.all(finalSelections.map((key) => this.apiClient.delete(key)))
      }

      // Check if delete was successful
      const isSuccess =
        res?.status === 200 ||
        (Array.isArray(res) && res.some((response) => response?.status === 200))

      if (isSuccess) {
        if (this.search) this.handleClearSearch()
        await this.getData({
          limit: this.limit,
          page: 1,
        })
        this.handleSortChange()
        this.handleUnselectAll()
        this.$refs['mc-modal-delete'].hide()
        this.$toast.success(this.$t('global.success.delete'))
      } else {
        // Extract error message
        let errorMessage = 'Delete failed'
        if (res && !Array.isArray(res)) {
          errorMessage = res.data?.message || res.statusText || errorMessage
        } else if (Array.isArray(res)) {
          const errorResponse = res.find((response) => response?.status !== 200)
          errorMessage = errorResponse?.data?.message || errorResponse?.statusText || errorMessage
        }
        this.handleError({ message: errorMessage })
      }
    },

    handleError(error) {
      if (!error) return
      this.$te(`master.${error.message}`)
        ? this.$toast.error(this.$t(`master.${error.message}`))
        : this.$toast.error(error.message)
    },

    handleSelect(checked, data) {
      this.selectedIds = { ...this.selectedIds, [`${data.item.id}`]: checked }
      if (checked) {
        let flag = true
        this.selectedIdsLength += 1
        const selectedKeys = Object.keys(this.selectedIds).filter((key) => this.selectedIds[key])
        for (const iterator of this.ids) {
          let index = selectedKeys.indexOf(iterator.toString())
          if (index == -1) {
            flag = false
            break
          }
        }

        if (flag) this.selectedAll = true
        else this.selectedAll = false
      } else {
        this.selectedAll = false
        this.selectedIdsLength -= 1
      }
      this.$emit('selectedIdsChange', this.selectedIds)
    },

    handleSelectAll(event) {
      if (event) {
        const selectAll = Object.fromEntries(this.ids.map((key) => [key, true]))
        this.selectedIds = Object.assign({}, this.selectedIds, selectAll)
        this.handleSelectAllOnPaginationChange()
      } else {
        const unselectAll = Object.fromEntries(this.ids.map((key) => [key, false]))
        this.selectedIds = Object.assign({}, this.selectedIds, unselectAll)
        this.selectedIdsLength = this.selectedIdsLength - this.ids.length
      }
      this.$emit('selectedIdsChange', this.selectedIds)
    },

    handleUnselectAll() {
      this.selectedIds = {}
      this.selectedAll = false
      this.selectedIdsLength = 0
      this.$emit('selectedIdsChange', this.selectedIds)
    },

    handleAdd() {
      if (this.addWithModal) {
        this.addWithModal()
        return
      }
      if (this.isEditingRow != null) return
      this.isEditingRow = 0
      this.isAdding = true

      this.$emit('available-change', true)
      this.$emit('changeData')
      window.addEventListener('keydown', Helper.methods.preventTabKey)

      this.items = [{ id: 0 }, ...this.items]
      if (this.fieldException?.length > 0) this.fields.map((e) => (e.editable = true))
    },
  },
}
</script>

<template>
  <div class="mc-editable-table">
    <div class="d-flex justify-content-between align-items-center">
      <div class="fw-bold text-nowrap card-title mb-0">{{ tableTitle }}</div>
      <div class="ml-auto d-flex justify-content-end mb-3">
        <McButton
          v-if="selectedIdsLength && enableDeleteButton"
          variant="secondary-red"
          class="mx-2"
          id="mc-delete-btn"
          :text="$t('global.delete')"
          icon="icon-outline_trash"
          @click="$refs['mc-modal-delete'].show()"
        />

        <McButton
          v-if="enableAddButton"
          variant="primary"
          :disabled="disabledGlobal || isAdding"
          id="mc-add-btn"
          text="Tambah"
          icon="icon-fill_plus"
          @click="handleAdd"
        />
      </div>
    </div>

    <div :class="{ 'card p-3 mt-2': withCard }">
      <b-row align-h="between">
        <b-col md="6" xl="10" :class="{ 'mt-0': withCard, 'mt-2': !withCard }">
          <h6 class="mc-row-check mt-0" v-if="selectedIdsLength">
            <span class="badge btn-success">{{ selectedIdsLength }}</span>
            Baris terpilih.
            <a @click="handleUnselectAll" class="color-primary">Bersihkan pilihan</a>
          </h6>
        </b-col>
        <b-col md="4" xl="2" v-if="searchable" :class="{ 'mt-0': withCard, 'mt-2': !withCard }">
          <b-input-group size="md" class="mc-has-search ps-1">
            <template #prepend>
              <i class="ps-2 mc-icon icon-fill_search"></i>
            </template>
            <b-form-input
              placeholder="Cari..."
              :disabled="disabledGlobal"
              :value="search"
              type="search"
              @input="handleSearchChange"
              ref="search-input-ref"
            ></b-form-input>
          </b-input-group>
        </b-col>
      </b-row>
      <FormulateForm
        v-model="formValues"
        name="editable-table-form"
        ref="formRef"
        @submit="handleSubmit"
        @failed-validation="handleFailedValidation"
      >
        <div class="table-responsive my-3 border border-1 mc-table-overflow">
          <b-table
            :busy="isBusy"
            :items="items"
            :fields="computedFields"
            :per-page="!hidePagination ? limit : totalCount"
            fixed
            show-empty
            class="editable-table"
            :filter="customSearch"
            :sortBy="sortBy"
          >
            <!-- Loading -->
            <template #table-busy>
              <div class="__busy">
                <b-spinner class="align-middle mb-2"></b-spinner>
                <strong>Loading...</strong>
              </div>
            </template>

            <!-- Empty Result -->
            <template #empty>
              <div class="text-center my-4">Tidak ada data</div>
            </template>

            <!-- Slot: Header Checkbox -->
            <template #head(checkbox)>
              <div
                class="form-check"
                style="width: 10px"
                v-if="enableDeleteButton || enableCheckbox"
              >
                <b-form-checkbox
                  @change="handleSelectAll"
                  v-model="selectedAll"
                  :disabled="disabledGlobal || items?.length === 0"
                ></b-form-checkbox>
              </div>
            </template>

            <!-- Slot: Header Data -->
            <template #head()="data">
              <div @click="handleSortChange(data)">
                {{ data.label }}
                <template v-if="data.field.isSortable">
                  <div v-if="showSortIcon(data, 'asc')" class="sort-icon-up">
                    <i class="mc-icon icon-outline_chevron-up"></i>
                  </div>
                  <div v-if="showSortIcon(data, 'desc')" class="sort-icon-down">
                    <i class="mc-icon icon-outline_chevron-down"></i>
                  </div>
                </template>
              </div>
            </template>

            <!-- Slot: Cell Checkbox -->
            <template #cell(checkbox)="data">
              <div class="form-check" v-if="enableDeleteButton">
                <b-form-checkbox
                  @change="(event) => handleSelect(event, data)"
                  :checked="selectedIds[data.item.id]"
                  :disabled="disabledGlobal"
                ></b-form-checkbox>
              </div>
            </template>

            <!-- Slot: All Cells -->
            <template #cell()="{ index, field, value, item }">
              <template v-if="(field.editable || field.isEditable) && isEditingRow == index">
                <FormulateInput
                  v-bind="{ ...field }"
                  :rowItem="item"
                  :label="null"
                  :name="field.inputName || field.key"
                  :type="field.type || 'text'"
                  :value="getInputInitialValue({ field, item })"
                  :initialObject="field.getInitialObject?.(item)"
                  :disabled="isSubmitting"
                  validation-behavior="live"
                  error-behavior="live"
                  :ref="`${field.key}-ref`"
                />
              </template>

              <!-- Bold First Column -->
              <template v-else-if="field.isRedirectable">
                <div class="fw-bold text-primary">
                  {{ value }}
                </div>
              </template>

              <template v-else-if="field.customColumn && isEditingRow !== index">
                <slot :name="field.key" v-bind="{ index, field, value, item }"></slot>
              </template>

              <template v-else-if="useThousandFormat">
                {{ transformCellValue(value) }}
              </template>
              <template v-else>
                {{ value }}
              </template>
            </template>

            <!-- Slot: Action Column (Last Column) -->
            <template #cell(action)="data">
              <div v-if="isEditingRow == data.index">
                <b-button variant="outline" class="__accept-button btn" type="submit">
                  <i class="mc-icon icon-fill_checkmark" />
                </b-button>

                <b-button
                  variant="outline"
                  class="__cancel-button btn"
                  @click="handleEditCancel(data)"
                >
                  <i class="mc-icon icon-outline_close" />
                </b-button>
              </div>

              <b-button
                v-else
                variant="outline"
                :class="{
                  '__edit-button btn': true,
                  __disabled: disabled || isEditingRow !== null || isBusy || !data.item.can_edit,
                }"
                @click="handleEditStart(data)"
              >
                <i
                  v-if="data.item.can_edit && isEnableEditButton"
                  class="mc-icon icon-outline_edit"
                ></i>
              </b-button>
            </template>
          </b-table>
        </div>
      </FormulateForm>
      <div class="d-flex flex-wrap flex-md-nowrap" v-if="!hidePagination">
        <div class="mc-limit">
          <div class="__limit">
            <div>Menampilkan</div>
            <b-form-select
              class="form-select form-select-sm __limit--dropdown"
              size="sm"
              :disabled="disabledGlobal"
              :options="limitOptions"
              :value="limit"
              @input="handleLimitChange"
            >
            </b-form-select>
            <div>data.</div>
          </div>

          <div class="__info" v-if="totalCount === 0">
            <strong>{{ totalCount }}</strong> dari <strong>{{ totalCount }}</strong> data.
          </div>

          <div class="__info" v-else>
            <strong>{{ startData }}</strong> - <strong>{{ endData }}</strong> dari
            <strong>{{ totalCount }}</strong> data.
          </div>
        </div>
        <div class="mc-pagination-container">
          <b-pagination
            :disabled="disabledGlobal"
            :per-page="limit"
            :total-rows="totalCount"
            :value="page"
            @change="handlePageChange"
          ></b-pagination>
        </div>
      </div>
    </div>
    <b-modal
      class="mc-modal"
      id="mc-modal-delete"
      ref="mc-modal-delete"
      title="Hapus"
      size="md"
      centered
      scrollable
    >
      <div class="card-body p-2">
        <p>
          Apakah Anda yakin ingin menghapus <strong>{{ selectedIdsLength }}</strong> data berikut ?
        </p>
      </div>
      <template #modal-footer="{ cancel }">
        <div class="d-flex">
          <McButton variant="secondary" class="me-2" text="Batal" @click="cancel()" />
          <McButton variant="primary" text="Hapus" @click="handleDelete" />
        </div>
      </template>
    </b-modal>
  </div>
</template>

<style lang="scss" scoped>
@import './style.scss';
</style>
